import { apiFetch } from './client'

/**
 * Guided SQL "Data Requests" API (PRD Phase 2 — docs/handoff/2026-06-10-01-phase2-sql-design.md §3).
 * The server filters tables to the company's plan layer access and validates
 * every selection; the viewer renders exactly what is advertised.
 */
export interface SqlTable {
  id: string
  label: string
}

export interface SqlColumn {
  name: string
  label: string
  type: 'string' | 'number' | 'date'
  preview: 'distinct' | 'sample'
}

export interface SqlFilter {
  column: string
  // v1.1: "contains" allowed only on string-type columns.
  // v1.2: ">=" / "<=" allowed only on number-type columns, and only with a
  // value that converts to a number. Both rules are server-validated — the
  // viewer mirrors them to build a sane form, it does not enforce them.
  op: '=' | 'contains' | '>=' | '<='
  value: string
}

export interface SqlQueryResult {
  rows: Array<Record<string, unknown> & { bbox_3857: [number, number, number, number] | null }>
  page: number
  page_size: number
  total: number
  total_is_capped: boolean
  data_as_of: string | null
}

export async function fetchSqlTables(): Promise<{ tables: SqlTable[]; data_as_of: string | null }> {
  const res = await apiFetch('/api/sql/tables')
  return (await res.json()) as { tables: SqlTable[]; data_as_of: string | null }
}

export async function fetchSqlColumns(table: string): Promise<SqlColumn[]> {
  const res = await apiFetch(`/api/sql/tables/${encodeURIComponent(table)}/columns`)
  const body = (await res.json()) as { columns: SqlColumn[] }
  return body.columns
}

export async function fetchSqlValues(
  table: string,
  column: string,
  q = '',
): Promise<{ kind: 'distinct' | 'sample'; values: string[] }> {
  // v1.1: non-empty q = type-ahead search on sample-kind columns (§3)
  const search = q === '' ? '' : `&q=${encodeURIComponent(q)}`
  const res = await apiFetch(
    `/api/sql/tables/${encodeURIComponent(table)}/values?column=${encodeURIComponent(column)}${search}`,
  )
  return (await res.json()) as { kind: 'distinct' | 'sample'; values: string[] }
}

export async function runSqlQuery(
  table: string,
  filters: SqlFilter[],
  page: number,
  pageSize: number,
): Promise<SqlQueryResult> {
  const res = await apiFetch('/api/sql/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ table, filters, page, page_size: pageSize }),
  })
  return (await res.json()) as SqlQueryResult
}
